// Builds the `@font-face` declarations for an organization's custom fonts that
// back the design-system text roles (title / subtitle / paragraph / …).
//
// The catch: a custom font family is uploaded as several *independently named*
// weight variants — e.g. "TT Norms Pro Regular" (400), "TT Norms Pro Medium"
// (500), "TT Norms Pro DemiBold" (600). A design-system role references exactly
// one of those titles (say the 400 "Regular"). If we only emit that single
// face, the family has no heavier glyphs, so **bold** text (font-weight:700,
// e.g. Froala's `<strong>`) has nothing real to resolve to — the browser either
// synthesizes a fake bold or renders it identically to the regular weight. That
// is the "bold looks the same in the preview" bug.
//
// Fix: for each role, pull in the other weight variants of the *same family*
// and register them **under the role's own family name** at their real weights.
// Bold text then resolves to a real heavier face (DemiBold) rather than a
// synthetic/absent one.
//
// Grouping is by name prefix, not by parsing weight descriptors ("Bold",
// "Light", …): the titles are free text the user types when uploading, so a
// descriptor list is unreliable. Variants of one family share a common prefix
// and differ only by a trailing extension token, so we group by "title minus
// its last word". This also keeps us from loading every unrelated custom font
// the org happens to have — only the used family's siblings are emitted.

export type CustomFont = {
  title: string;
  url: string;
  weight: number;
  type?: string;
};

const primaryName = (title: string): string => title.split(",")[0].trim();

/**
 * The family prefix shared by a font's weight variants: the title with its
 * trailing extension token removed. "TT Norms Pro DemiBold" -> "TT Norms Pro".
 * Single-word titles (e.g. "Montserrat") are returned unchanged.
 */
export const getFamilyPrefix = (title: string): string => {
  const primary = primaryName(title);
  const lastSpace = primary.lastIndexOf(" ");
  if (lastSpace === -1) return primary;
  return primary.slice(0, lastSpace).trim();
};

export type FontFace = { family: string; url: string; weight: number };

/**
 * Resolve the set of `@font-face` descriptors needed for the given design-system
 * role fonts, expanding each role family to include the sibling weight variants
 * that share its name prefix so bold text can resolve to a real face.
 */
export const buildDesignSystemFontFaceList = (
  customFonts: CustomFont[] | undefined,
  roleFontTitles: (string | undefined | null)[],
): FontFace[] => {
  if (!customFonts?.length) return [];

  const roleTitles = Array.from(
    new Set(
      roleFontTitles
        .filter((t): t is string => typeof t === "string" && t.length > 0)
        .map(primaryName),
    ),
  );
  if (!roleTitles.length) return [];

  // Group every custom font by its family prefix for sibling lookup.
  const byPrefix = new Map<string, CustomFont[]>();
  for (const font of customFonts) {
    const prefix = getFamilyPrefix(font.title);
    const list = byPrefix.get(prefix);
    if (list) list.push(font);
    else byPrefix.set(prefix, [font]);
  }

  const faces: FontFace[] = [];

  for (const roleTitle of roleTitles) {
    // Role font isn't one of the org's custom fonts (e.g. a Google font) —
    // nothing to emit; it loads via the Google Fonts import instead.
    const isCustom = customFonts.some((f) => primaryName(f.title) === roleTitle);
    if (!isCustom) continue;

    const siblings = byPrefix.get(getFamilyPrefix(roleTitle)) ?? [];

    // De-dupe by weight; prefer the variant whose title matches the role for
    // its own weight so the family's natural weight uses the intended file.
    const perWeight = new Map<number, CustomFont>();
    for (const font of siblings) {
      const existing = perWeight.get(font.weight);
      if (!existing || primaryName(font.title) === roleTitle) {
        perWeight.set(font.weight, font);
      }
    }

    for (const font of Array.from(perWeight.values())) {
      faces.push({ family: roleTitle, url: font.url, weight: font.weight });
    }
  }

  return faces;
};

/**
 * String form of {@link buildDesignSystemFontFaceList} — the CSS injected into
 * the editor canvas and the published/preview `<style>`.
 */
export const buildDesignSystemFontFaces = (
  customFonts: CustomFont[] | undefined,
  roleFontTitles: (string | undefined | null)[],
): string =>
  buildDesignSystemFontFaceList(customFonts, roleFontTitles)
    .map(
      ({ family, url, weight }) =>
        `@font-face{font-family:"${family}";src:url('${url}');font-weight:${weight};font-style:normal;font-display:swap}`,
    )
    .join("");
